import React, { FC, useEffect, useRef, useState } from 'react';
import 'react-date-range/dist/styles.css';
import { OrderFilterSection } from '../../components/orders/OrderFilterSection';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { PageTitle } from '../../layout/core';
import clsx from 'clsx';
import {
	exportCSVOrderTable, fetchOrdersList,
	fetchOrderStatuses, getOrdersListQueryParamsV2, IOrderFilters, savedOrderFilters,
	setOrderPagination, setOrderDataToDefault,
	setOrderSorting,
} from '../../store/order/actions/order';
import { useOrdersContext } from '../../store/order/order-context';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import { useLocation } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { getTableSort, getTableSortField } from '../../utils/linksHelper';
import './OrdersList.scss';
import { ISelectorData } from '../../components/application/NewApplication';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { ExportCSVButton } from '../../components/form/ExportCSVButton';

export interface IOrderListPageParams {
	pageSize?: number,
	pageNumber?: number,
	query?: string,
	sort?: string,
	sortField?: string,
	status?: number[] | string[] | string,
	mid?: string,
	timezone?: string,
	placedFrom?: string | number | Date | undefined,
	placedTo?: string | number | Date | undefined,
	from?: string | number | Date | undefined,
	to?: string | number | Date | undefined,
	types?: string;
	isMIT?: boolean;
	sid?: string;
	orderId?: string;
	externalOrderId?: string;
	email?: string;
	firstName?: string;
	lastName?: string;
}

export const OrdersList: FC = () => {

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const isMounted = useRef(false);

	const [refreshFilters, setRefreshFilters] = useState<boolean>(false);

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { orderContextState, dispatch } = useOrdersContext();
	const {
		orderData,
		orderTotalItemCount,
		isExportCSVLoading,
		isOrderDataLoading,
		orderSorting,
		isOrderStatusesLoading,
		orderLinks,
	} = orderContextState;

	const { merchantState: { chosenMerchant, isPartnerNamesListLoading }} = useMerchants();

	const getStatusFilter = (orderStatusesList: ISelectorData[]): string[] => {
		const statuses = [];
		orderStatusesList?.forEach(el => {
			// if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN)) {
			// 	return statuses.push(el.value);
			// } else if (el.label.toLowerCase() === 'completed') {
			// 	return statuses.push(el.value);
			// }
			if (!checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && el.label.toLowerCase() === 'completed') {
				return statuses.push(el.value);
			}
		});

		return statuses;
	};

	useEffect(() => {
		if (!isMounted.current) return;

		const parsedSearchParams = parseSearchParams(search) || {};

		parsedSearchParams.sort = getTableSort(orderSorting);
		parsedSearchParams.sortField = getTableSortField(orderSorting);
		parsedSearchParams.mid = chosenMerchant;
		parsedSearchParams.pageNumber = 1;

		if (!parsedSearchParams.timezone) {
			// default value
			parsedSearchParams.timezone = 'UTC';
		}

		if (!parsedSearchParams.pageSize) {
			// default value
			parsedSearchParams.pageSize = 10;
		}

		navigate(`${pathname}?${makeSearchParams(parsedSearchParams)}`);
	}, [orderSorting, chosenMerchant]);

	useEffect(() => {
		return () => {
			setOrderDataToDefault(dispatch);
		};
	}, []);

	useEffect(() => {
		if (!isMounted.current) {
			// first mount
			isMounted.current = true;
		}
	}, []);

	const listener = async (search: any): Promise<void> => {
		if (!chosenMerchant && !search?.mid) {
			const merchantId = localStorage.getItem('chosenMerchant');
			if (merchantId) {
				return;
			}
		}

		let localSearch = {};

		const orderStatusesList = await fetchOrderStatuses(dispatch);

		if (Object.keys(search).length) {
			localSearch = search;
		} else {

			const initialValues: IOrderFilters = {
				status: getStatusFilter(orderStatusesList),
				timezone: 'UTC',
				pageNumber: '0',
				pageSize: '10',
				// sort: 'false',
				// sortField: 'CreatedOn',
			};
			if (chosenMerchant) {
				initialValues.mid = chosenMerchant;
			}
			const formattedInitialData = getOrdersListQueryParamsV2({
				state: orderContextState,
				filterData: initialValues
			});

			localSearch = formattedInitialData;
			savedOrderFilters(dispatch, formattedInitialData, orderStatusesList);
		}

		void fetchOrdersList(dispatch, localSearch, setRefreshFilters);
	};

	const rowActions = (rowData): JSX.Element => {
		const basePath = `${pathname}/${rowData?.original?.id}`;
		const isGhostMode = rowData?.original?.isGhostMode;
		let orderDetailsPath = basePath;
		let orderLogPath = `${basePath}/activity`;
		if (isGhostMode) {
			orderDetailsPath += '?isGhostMode=true';
			orderLogPath += '?isGhostMode=true';
		}
		return (
			<div className="d-flex align-items-center justify-content-center">
				<div className={clsx('me-2', rowData?.original?.isGhostMode && 'ghost-label')} />
				<Link
					to={orderDetailsPath}
					className="text-muted fs-7 fw-bolder"
				>
					View
				</Link>
				{
					checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
						<Link
							to={orderLogPath}
							state={{ isGhostMode }}
							className="text-muted fs-7 fw-bolder ms-2 ps-2 action-log-border"
						>
							Log
						</Link>
					)
				}
			</div>
		);
	};

	return (
		<>
			<PageTitle breadcrumbs={[]}>Orders</PageTitle>
			<OrderFilterSection setRefreshFilters={setRefreshFilters} />

			<Table
				name={TablesNames.orderTableData}
				data={orderData || []}
				sorting={orderSorting}
				isLoading={isOrderStatusesLoading || isOrderDataLoading || isPartnerNamesListLoading}
				totalItemCount={orderTotalItemCount}
				setSorting={(value): void => setOrderSorting(dispatch, value)}
				setPagination={(value): void => setOrderPagination(dispatch, value)}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={checkRoles(authUser, CLAIMS.SUPER_ADMIN) ? 100 : 50}
				paginationLinks={orderLinks}
				isRefreshed={refreshFilters}
				toolbarCustomAction={
					<ExportCSVButton
						orderTotalItemCount={orderTotalItemCount}
						isExportCSVLoading={isExportCSVLoading}
						exportCSV={async (): Promise<void> => await exportCSVOrderTable(dispatch, chosenMerchant, search)}
					/>
				}
				queryParamsListener={listener}
			/>

		</>
	);
};
